// src/rms/ads/AdUnit.js
// ============================================================================
// The rendered ad creative
// ============================================================================
// Three things happen here that matter to revenue:
//
//   1. LAZY RENDER — the creative is not requested until the slot is near the
//      viewport. An ad rendered off-screen is an ad request nobody can pay for.
//   2. VIEWABLE COUNTING — the impression is logged only after the MRC
//      threshold (50% visible, 1 continuous second) is met, so reported
//      delivery matches what an advertiser would actually buy.
//   3. FIXED IAB GEOMETRY — the box reserves its exact size before anything
//      loads, which both prevents layout shift and lets affiliate banners drop
//      in unmodified.
//
// Passive by construction: an inline block in normal document flow. No overlay,
// no modal, no takeover.
// ============================================================================

import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DOMPurify from "dompurify";
import { useRms } from "../context/RmsContext";
import adEngine, { logImpression, logClick } from "./adEngine";
import useViewability from "./useViewability";
import { sectionOf } from "./useSectionRotation";
import AdSenseUnit from "./AdSenseUnit";
import i18n from "../i18n";

/**
 * One field of a creative, translated if it is ours to translate.
 *
 * House ads carry headlineKey / bodyKey / ctaKey and follow the screen's
 * language. An ADVERTISER's creative carries none, falls through to the English
 * it was sold with, and stays that way on purpose -- translating somebody
 * else's advertisement puts words in their mouth, and they bought the words
 * they wrote.
 */
const copy = (creative, field) => {
  const key = creative[`${field}Key`];
  return key ? i18n.t(key, { defaultValue: creative[field] }) : creative[field];
};

function AdUnit({ slot, slotKey, campaign, variant = "auto", style = {} }) {
  const { operator, config, t } = useRms();
  const navigate = useNavigate();
  const location = useLocation();

  const screen = sectionOf(location.pathname);

  const { ref, inView, viewable, reset } = useViewability({
    enabled: !!campaign,
    onViewable: () => {
      logImpression({ operator, config, slotKey, campaign, screen });
    },
  });

  // A new creative must earn its own viewable impression.
  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.id]);

  if (!campaign) return null;

  const { creative } = campaign;
  const isBanner = variant === "banner" || (variant === "auto" && slot.placement.startsWith("banner"));

  const handleClick = async () => {
    await logClick({ operator, config, slotKey, campaign, screen });
    adEngine.recordAction();
    if (creative.internalHref) {
      navigate(creative.internalHref);
    } else if (creative.clickUrl || creative.click_url) {
      window.open(creative.clickUrl || creative.click_url, "_blank", "noopener,noreferrer");
    }
  };

  // Reserve the exact slot size up front so nothing reflows when the creative
  // arrives, and so an affiliate banner lands in a box built for it.
  const frame = {
    display: "block",
    width: isBanner ? "100%" : slot.width,
    maxWidth: slot.width,
    minHeight: slot.height,
    ...style,
  };

  return (
    <aside
      ref={ref}
      className={`rms-ad rms-ad--${isBanner ? "banner" : "rail"}`}
      aria-label={t("adUnit.advertisement", "Advertisement")}
      data-slot={slotKey}
      data-viewable={viewable ? "true" : "false"}
      style={frame}
    >
      <div className="rms-ad-tag">
        <span>{campaign.isAffiliate ? t("adUnit.affiliate", "Affiliate") : t("adUnit.ad", "Ad")}</span>
        {/* A network fill has no advertiser we can name honestly -- the buyer is
            whoever won Google's auction this second, not Google. The "Ad" label
            still stands, which is what disclosure actually requires. */}
        {campaign.advertiser ? (
          <span className="rms-ad-advertiser">{campaign.advertiser}</span>
        ) : null}
      </div>

      {/* Nothing is fetched until the slot approaches the viewport. */}
      {!inView ? (
        <div className="rms-ad-placeholder" style={{ height: slot.height - 24 }} aria-hidden="true" />
      ) : (
        <AdCreative
          creative={creative}
          slot={slot}
          isBanner={isBanner}
          onClick={handleClick}
        />
      )}
    </aside>
  );
}

// ----------------------------------------------------------------------------

function AdCreative({ creative, slot, isBanner, onClick }) {
  const type = creative.type || "native";

  // --- network fill ----------------------------------------------------------
  // Declared at the slot's exact IAB size, because AdSense fill rates depend on
  // the box matching a size advertisers actually bid on. That makes the tile a
  // little taller than a house ad -- the tag row sits above the full 250 rather
  // than inside it -- which is the correct trade.
  if (type === "adsense") {
    return <AdSenseUnit placement={creative.placement} width={slot.width} height={slot.height} />;
  }

  // --- affiliate image banner ------------------------------------------------
  // rel="sponsored nofollow noopener" is required by Google's link-attribution
  // policy for paid and affiliate placements.
  if (type === "image") {
    return (
      <a
        href={creative.clickUrl}
        target="_blank"
        rel="sponsored nofollow noopener noreferrer"
        onClick={onClick}
        className="rms-ad-image"
        style={{ display: "block", lineHeight: 0 }}
      >
        <img
          src={creative.imageUrl}
          alt={creative.alt || ""}
          width={creative.width || slot.width}
          height={creative.height || slot.height}
          loading="lazy"
          style={{ display: "block", width: "100%", height: "auto" }}
        />
      </a>
    );
  }

  // --- raw affiliate/network snippet ----------------------------------------
  // Rendered inside a sandboxed iframe, which is how real ad slots work: the
  // advertiser's scripts get no access to the operator's DOM, storage or
  // session. allow-scripts is needed for the creative to run at all;
  // allow-same-origin is deliberately NOT granted, which is what keeps the
  // sandbox meaningful.
  if (type === "html") {
    return (
      <iframe
        title={`Advertisement — ${creative.alt || "sponsored"}`}
        srcDoc={DOMPurify.sanitize(creative.html || "", { WHOLE_DOCUMENT: true, ADD_TAGS: ["iframe"] })}
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer-when-downgrade"
        style={{ display: "block", width: "100%", height: slot.height - 24, border: 0 }}
      />
    );
  }

  // --- our own styled card ---------------------------------------------------
  // copy() below resolves *Key when there is one. Only house ads carry them.
  return (
    <button type="button" onClick={onClick} className="rms-ad-body">
      <span aria-hidden="true" className="rms-ad-accent" style={{ background: creative.accent || "#334155" }} />
      <span className="rms-ad-copy">
        <span className="rms-ad-headline">{copy(creative, "headline")}</span>
        <span className="rms-ad-text">{copy(creative, "body")}</span>
      </span>
      <span className="rms-ad-cta" style={{ color: creative.accent || "#334155" }}>
        {copy(creative, "cta")} →
      </span>
    </button>
  );
}

export default AdUnit;
